/**
 * Smart compare defaults utility
 *
 * Generates intelligent defaults for compare mode based on available data.
 * Does NOT merge with user config - that's handled by the caller.
 */

import {
  type ColumnConfig,
  type CompareSettings,
  type TileControls,
} from '../../common';
import {type StepMeta} from '../../types';

/**
 * Builds column configurations for the variable dimension.
 * Other dimensions are linked (read from parent config).
 */
function buildColumns(
  variable: 'mediaKey' | 'run' | 'step' | 'index',
  mediaKeys: string[],
  runNames: string[],
  stepMeta: StepMeta,
  columnCount: number
): ColumnConfig[] {
  // Run has special duplication logic for single-run projects
  if (variable === 'run') {
    const runs = runNames.slice(0, columnCount);
    const runsToUse =
      runs.length === 1 ? Array(columnCount).fill(runs[0]) : runs;
    return runsToUse.map(run => ({run}));
  }

  // Map dimension to its data source
  const data = {
    mediaKey: mediaKeys,
    step: stepMeta.steps,
    index: stepMeta.indices,
  }[variable];
  return data.slice(0, columnCount).map(item => ({[variable]: item}));
}

/**
 * Generates smart defaults for compare mode (no user config merging).
 *
 * Decision tree:
 * 1. Multiple media keys → vary mediaKey
 * 2. Multiple runs → vary run
 * 3. Multiple steps → vary step
 * 4. Multiple indices → vary index
 * 5. Fallback → vary run (duplicate if single run)
 *
 * @returns Smart default compareSettings (caller handles merging with user config)
 */
export function getCompareConfigDefaults(
  mediaKeys: string[],
  runNames: string[],
  stepMeta: StepMeta,
  columnCount: number
): CompareSettings {
  // Fallback when no data
  if (!runNames.length) {
    return {
      columns: [],
      tileControls: {
        mediaKey: 'linked',
        run: 'individual',
        step: 'linked',
        index: 'linked',
      },
    };
  }

  // Build tile controls: variable is 'individual', others are 'linked'
  const tileControls: TileControls = {
    run: 'individual',
    mediaKey: 'linked',
    step: 'linked',
    index: 'linked',
  };

  // Build columns for the variable dimension
  const columns = buildColumns(
    'run',
    mediaKeys,
    runNames,
    stepMeta,
    columnCount
  );

  // Safety: if no columns, create 2 columns with first run
  if (columns.length === 0) {
    return {
      columns: [{run: runNames[0]}, {run: runNames[0]}],
      tileControls,
    };
  }

  return {columns, tileControls};
}
